import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// TYPES
// ============================================================================

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  shortcut?: string;
  group?: string;
  keywords?: string[];
  disabled?: boolean;
  onSelect?: () => void;
}

export interface CommandGroup {
  id: string;
  label: string;
  commands: CommandItem[];
}

// ============================================================================
// SEARCH ICON
// ============================================================================

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

// ============================================================================
// COMMAND PALETTE CONTEXT
// ============================================================================

interface CommandPaletteContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const CommandPaletteContext = React.createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette() {
  const context = React.useContext(CommandPaletteContext);
  if (!context) {
    throw new Error('useCommandPalette must be used within a CommandPaletteProvider');
  }
  return context;
}

// ============================================================================
// COMMAND PALETTE PROVIDER
// ============================================================================

export interface CommandPaletteProviderProps {
  children: React.ReactNode;
  shortcut?: string;
}

export function CommandPaletteProvider({
  children,
  shortcut = 'k',
}: CommandPaletteProviderProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = React.useCallback(() => setIsOpen((prev) => !prev), []);

  // Global keyboard shortcut
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === shortcut) {
        event.preventDefault();
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcut, toggle]);

  const value = React.useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle]
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

// ============================================================================
// COMMAND PALETTE INPUT
// ============================================================================

export interface CommandPaletteInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function CommandPaletteInput({
  value,
  onChange,
  placeholder = 'Type a command or search...',
  className,
}: CommandPaletteInputProps) {
  return (
    <div className={clsx('relative flex items-center', className)}>
      <SearchIcon className="absolute left-4 w-5 h-5 text-gray-400 dark:text-gray-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={clsx(
          'w-full pl-12 pr-4 py-3 text-base bg-transparent border-0 outline-none',
          'text-gray-900 dark:text-white placeholder:text-gray-400'
        )}
        autoComplete="off"
        autoFocus
      />
    </div>
  );
}

// ============================================================================
// COMMAND PALETTE EMPTY
// ============================================================================

export interface CommandPaletteEmptyProps {
  children?: React.ReactNode;
  className?: string;
}

export function CommandPaletteEmpty({
  children = 'No results found.',
  className,
}: CommandPaletteEmptyProps) {
  return (
    <div
      className={clsx(
        'py-8 text-center text-sm text-gray-500 dark:text-gray-400',
        className
      )}
    >
      {children}
    </div>
  );
}

// ============================================================================
// COMMAND PALETTE LOADING
// ============================================================================

export interface CommandPaletteLoadingProps {
  children?: React.ReactNode;
  className?: string;
}

export function CommandPaletteLoading({
  children = 'Loading...',
  className,
}: CommandPaletteLoadingProps) {
  return (
    <div
      className={clsx(
        'py-8 text-center text-sm text-gray-500 dark:text-gray-400',
        className
      )}
    >
      <div className="flex items-center justify-center gap-2">
        <svg
          className="animate-spin w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// COMMAND PALETTE GROUP
// ============================================================================

export interface CommandPaletteGroupProps {
  heading: string;
  children: React.ReactNode;
  className?: string;
}

export function CommandPaletteGroup({
  heading,
  children,
  className,
}: CommandPaletteGroupProps) {
  return (
    <div className={clsx('py-2', className)}>
      <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        {heading}
      </div>
      {children}
    </div>
  );
}

// ============================================================================
// COMMAND PALETTE ITEM
// ============================================================================

export interface CommandPaletteItemProps {
  command: CommandItem;
  isActive?: boolean;
  onSelect: (command: CommandItem) => void;
  onHover?: (command: CommandItem) => void;
  className?: string;
}

export function CommandPaletteItem({
  command,
  isActive = false,
  onSelect,
  onHover,
  className,
}: CommandPaletteItemProps) {
  const handleClick = () => {
    if (!command.disabled) {
      command.onSelect?.();
      onSelect(command);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => onHover?.(command)}
      disabled={command.disabled}
      className={clsx(
        'w-full flex items-center justify-between gap-4 px-4 py-2.5 text-left transition-colors',
        isActive && !command.disabled
          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
          : 'text-gray-700 dark:text-gray-300',
        command.disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-gray-100 dark:hover:bg-gray-700',
        className
      )}
      aria-selected={isActive}
      role="option"
    >
      <div className="flex items-center gap-3 min-w-0">
        {command.icon && (
          <span className="flex-shrink-0 w-5 h-5 text-gray-400 dark:text-gray-500">{command.icon}</span>
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{command.label}</div>
          {command.description && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {command.description}
            </div>
          )}
        </div>
      </div>
      {command.shortcut && (
        <kbd
          className={clsx(
            'flex-shrink-0 px-2 py-1 text-xs font-mono rounded',
            'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
          )}
        >
          {command.shortcut}
        </kbd>
      )}
    </button>
  );
}

// ============================================================================
// COMMAND PALETTE LIST
// ============================================================================

export interface CommandPaletteListProps {
  children: React.ReactNode;
  className?: string;
}

export function CommandPaletteList({ children, className }: CommandPaletteListProps) {
  return (
    <div
      className={clsx('max-h-[300px] overflow-y-auto', className)}
      role="listbox"
    >
      {children}
    </div>
  );
}

// ============================================================================
// COMMAND PALETTE FOOTER
// ============================================================================

export interface CommandPaletteFooterProps {
  children?: React.ReactNode;
  className?: string;
}

export function CommandPaletteFooter({ children, className }: CommandPaletteFooterProps) {
  const defaultHints = (
    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
      <span className="flex items-center gap-1">
        <kbd className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 font-mono">↑↓</kbd>
        <span>to navigate</span>
      </span>
      <span className="flex items-center gap-1">
        <kbd className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 font-mono">↵</kbd>
        <span>to select</span>
      </span>
      <span className="flex items-center gap-1">
        <kbd className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 font-mono">esc</kbd>
        <span>to close</span>
      </span>
    </div>
  );

  return (
    <div
      className={clsx(
        'px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50',
        className
      )}
    >
      {children || defaultHints}
    </div>
  );
}

// ============================================================================
// FILTER COMMANDS UTILITY
// ============================================================================

export function filterCommands(
  commands: CommandItem[],
  query: string
): CommandItem[] {
  if (!query.trim()) return commands;

  const lowerQuery = query.toLowerCase();
  return commands.filter((command) => {
    const labelMatch = command.label.toLowerCase().includes(lowerQuery);
    const descMatch = command.description?.toLowerCase().includes(lowerQuery);
    const keywordMatch = command.keywords?.some((k) =>
      k.toLowerCase().includes(lowerQuery)
    );
    return labelMatch || descMatch || keywordMatch;
  });
}

export function filterGroups(
  groups: CommandGroup[],
  query: string
): CommandGroup[] {
  return groups
    .map((group) => ({
      ...group,
      commands: filterCommands(group.commands, query),
    }))
    .filter((group) => group.commands.length > 0);
}

// ============================================================================
// COMMAND PALETTE
// ============================================================================

export interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  commands?: CommandItem[];
  groups?: CommandGroup[];
  onSelect?: (command: CommandItem) => void;
  placeholder?: string;
  emptyMessage?: React.ReactNode;
  loading?: boolean;
  loadingMessage?: React.ReactNode;
  showFooter?: boolean;
  footer?: React.ReactNode;
  className?: string;
}

export function CommandPalette({
  isOpen,
  onClose,
  commands = [],
  groups = [],
  onSelect,
  placeholder,
  emptyMessage,
  loading = false,
  loadingMessage,
  showFooter = true,
  footer,
  className,
}: CommandPaletteProps) {
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);
  const dialogRef = React.useRef<HTMLDivElement>(null);

  // Flatten groups into commands if groups are provided
  const allCommands = React.useMemo(() => {
    if (groups.length > 0) {
      return groups.flatMap((g) => g.commands);
    }
    return commands;
  }, [commands, groups]);

  // Filter commands based on query
  const filteredCommands = React.useMemo(() => {
    return filterCommands(allCommands, query);
  }, [allCommands, query]);

  const filteredGroups = React.useMemo(() => {
    return filterGroups(groups, query);
  }, [groups, query]);

  // Reset state when closing
  React.useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [isOpen]);

  // Reset active index when filtered results change
  React.useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Keyboard navigation
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex((prev) =>
            prev < filteredCommands.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : filteredCommands.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (filteredCommands[activeIndex] && !filteredCommands[activeIndex].disabled) {
            const cmd = filteredCommands[activeIndex];
            cmd.onSelect?.();
            onSelect?.(cmd);
            onClose();
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeIndex, filteredCommands, onSelect, onClose]);

  // Click outside to close
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleSelect = (command: CommandItem) => {
    onSelect?.(command);
    onClose();
  };

  const handleHover = (command: CommandItem) => {
    const index = filteredCommands.findIndex((c) => c.id === command.id);
    if (index !== -1) {
      setActiveIndex(index);
    }
  };

  if (!isOpen) return null;

  const hasGroups = groups.length > 0 && filteredGroups.length > 0;
  const hasCommands = commands.length > 0 && filteredCommands.length > 0;
  const isEmpty = !loading && !hasGroups && !hasCommands;

  // Calculate active index for each command in group mode
  let commandIndex = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 animate-in fade-in-0 duration-200"
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className={clsx(
          'relative w-full max-w-xl mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl',
          'border border-gray-200 dark:border-gray-700 overflow-hidden',
          'animate-in fade-in-0 zoom-in-95 slide-in-from-top-4 duration-200',
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        {/* Input */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <CommandPaletteInput
            value={query}
            onChange={setQuery}
            placeholder={placeholder}
          />
        </div>

        {/* Content */}
        <CommandPaletteList>
          {loading && <CommandPaletteLoading>{loadingMessage}</CommandPaletteLoading>}

          {isEmpty && <CommandPaletteEmpty>{emptyMessage}</CommandPaletteEmpty>}

          {!loading && hasGroups &&
            filteredGroups.map((group) => (
              <CommandPaletteGroup key={group.id} heading={group.label}>
                {group.commands.map((cmd) => {
                  const currentIndex = commandIndex++;
                  return (
                    <CommandPaletteItem
                      key={cmd.id}
                      command={cmd}
                      isActive={currentIndex === activeIndex}
                      onSelect={handleSelect}
                      onHover={handleHover}
                    />
                  );
                })}
              </CommandPaletteGroup>
            ))}

          {!loading && !hasGroups && hasCommands &&
            filteredCommands.map((cmd, index) => (
              <CommandPaletteItem
                key={cmd.id}
                command={cmd}
                isActive={index === activeIndex}
                onSelect={handleSelect}
                onHover={handleHover}
              />
            ))}
        </CommandPaletteList>

        {/* Footer */}
        {showFooter && <CommandPaletteFooter>{footer}</CommandPaletteFooter>}
      </div>
    </div>
  );
}

// ============================================================================
// USE COMMAND PALETTE STATE HOOK
// ============================================================================

export interface UseCommandPaletteStateOptions {
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export interface UseCommandPaletteStateReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function useCommandPaletteState(
  options: UseCommandPaletteStateOptions = {}
): UseCommandPaletteStateReturn {
  const { defaultOpen = false, onOpenChange } = options;
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const open = React.useCallback(() => {
    setIsOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);

  const close = React.useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const toggle = React.useCallback(() => {
    setIsOpen((prev) => {
      onOpenChange?.(!prev);
      return !prev;
    });
  }, [onOpenChange]);

  return { isOpen, open, close, toggle };
}
